<script setup lang="ts">
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import { useLayout, applicaLayout } from '@/layout/composables/layout';
import { primaryColorsPreset, surfacesPreset } from '@/layout/composables/temiPreset';
import { updatePreset, updateSurfacePalette } from '@primeuix/themes';
import Material from '@primeuix/themes/material';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/stores/auth';

defineProps({
    simple: {
        type: Boolean,
        default: false
    }
});

const { layoutState, layoutConfig, isDarkTheme, changeMenuMode, toggleConfigSidebar, resetLayout } = useLayout();

const { t } = useI18n();
const themeOptions = computed(() => [
    { name: t('layout.config.chiaro', 'Chiaro'), value: false },
    { name: t('layout.config.scuro', 'Scuro'), value: true }
]);
const toast = useToast();
const auth = useAuthStore();
const darkTheme = ref(layoutConfig.darkTheme);
const menuMode = ref(layoutConfig.menuMode);
const menuProfilePosition = ref(layoutConfig.menuProfilePosition);

const primaryColors = computed(() => primaryColorsPreset);

const surfaces = ref(surfacesPreset);

const menuThemes = ref([
    { name: 'light', color: '#FDFEFF' },
    { name: 'dark', color: '#434B54' },
    { name: 'indigo', color: '#1A237E' },
    { name: 'bluegrey', color: '#37474F' },
    { name: 'brown', color: '#4E342E' },
    { name: 'cyan', color: '#006064' },
    { name: 'green', color: '#2E7D32' },
    { name: 'deeppurple', color: '#4527A0' },
    { name: 'deeporange', color: '#BF360C' },
    { name: 'pink', color: '#880E4F' },
    { name: 'purple', color: '#6A1B9A' },
    { name: 'teal', color: '#00695C' }
]);

const topbarThemes = ref([
    { name: 'lightblue', color: '#2E88FF' },
    { name: 'dark', color: '#363636' },
    { name: 'white', color: '#FDFEFF' },
    { name: 'blue', color: '#1565C0' },
    { name: 'deeppurple', color: '#4527A0' },
    { name: 'purple', color: '#6A1B9A' },
    { name: 'pink', color: '#AD1457' },
    { name: 'cyan', color: '#0097A7' },
    { name: 'teal', color: '#00796B' },
    { name: 'green', color: '#43A047' },
    { name: 'lightgreen', color: '#689F38' },
    { name: 'lime', color: '#AFB42B' },
    { name: 'yellow', color: '#FBC02D' },
    { name: 'amber', color: '#FFA000' },
    { name: 'orange', color: '#FB8C00' },
    { name: 'deeporange', color: '#D84315' },
    { name: 'brown', color: '#5D4037' },
    { name: 'grey', color: '#616161' },
    { name: 'bluegrey', color: '#546E7A' },
    { name: 'indigo', color: 'var(--p-primary-600)' } // segue il primario (vedi _topbar_indigo.scss)
]);

function executeDarkModeToggle() {
    layoutConfig.darkTheme = !layoutConfig.darkTheme;
    layoutConfig.menuTheme = isDarkTheme.value ? 'dark' : 'light';

    document.documentElement.classList.toggle('app-dark');
}

function toggleDarkMode() {
    if (!document.startViewTransition) {
        executeDarkModeToggle();

        return;
    }

    document.startViewTransition(() => executeDarkModeToggle(event));
}

function getPresetExt() {
    const color = primaryColors.value.find((c) => c.name === layoutConfig.primary);

    if (color.name === 'noir') {
        return {
            semantic: {
                primary: {
                    50: '{surface.50}',
                    100: '{surface.100}',
                    200: '{surface.200}',
                    300: '{surface.300}',
                    400: '{surface.400}',
                    500: '{surface.500}',
                    600: '{surface.600}',
                    700: '{surface.700}',
                    800: '{surface.800}',
                    900: '{surface.900}',
                    950: '{surface.950}'
                },
                colorScheme: {
                    light: {
                        primary: {
                            color: '{primary.950}',
                            contrastColor: '#ffffff',
                            hoverColor: '{primary.800}',
                            activeColor: '{primary.700}'
                        },
                        highlight: {
                            background: '{primary.950}',
                            focusBackground: '{primary.700}',
                            color: '#ffffff',
                            focusColor: '#ffffff'
                        }
                    },
                    dark: {
                        primary: {
                            color: '{primary.50}',
                            contrastColor: '{primary.950}',
                            hoverColor: '{primary.200}',
                            activeColor: '{primary.300}'
                        },
                        highlight: {
                            background: '{primary.50}',
                            focusBackground: '{primary.300}',
                            color: '{primary.950}',
                            focusColor: '{primary.950}'
                        }
                    }
                }
            }
        };
    } else {
        return {
            semantic: {
                primary: color.palette,
                colorScheme: {
                    light: {
                        primary: {
                            color: '{primary.500}',
                            contrastColor: '#ffffff',
                            hoverColor: '{primary.400}',
                            activeColor: '{primary.300}'
                        },
                        highlight: {
                            background: 'color-mix(in srgb, {primary.color}, transparent 88%)',
                            focusBackground: 'color-mix(in srgb, {primary.color}, transparent 76%)',
                            color: '{primary.700}',
                            focusColor: '{primary.800}'
                        }
                    },
                    dark: {
                        primary: {
                            color: '{primary.400}',
                            contrastColor: '{surface.900}',
                            hoverColor: '{primary.300}',
                            activeColor: '{primary.200}'
                        },
                        highlight: {
                            background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
                            focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
                            color: 'rgba(255,255,255,.87)',
                            focusColor: 'rgba(255,255,255,.87)'
                        }
                    }
                }
            }
        };
    }
}

function updateColors(type, color) {
    if (type === 'primary') {
        layoutConfig.primary = color.name;
    } else if (type === 'surface') {
        layoutConfig.surface = color.name;
    }

    applyTheme(type, color);
}

function applyTheme(type, color) {
    if (type === 'primary') {
        updatePreset(getPresetExt());
    } else if (type === 'surface') {
        updateSurfacePalette(color.palette);
    }
}

function setProfilePosition(value) {
    layoutConfig.menuProfilePosition = value;
}

// Al boot riapplica il tema persistito (colore primario / superficie) SOLO se personalizzato,
// così il preset Indigo di default (main.ts) resta intatto quando l'utente non l'ha cambiato.
// menuMode/temi/profilo si ripristinano da soli (classi reattive legate a layoutConfig).
onMounted(() => {
    if (layoutConfig.primary && layoutConfig.primary !== 'indigo') {
        const primary = primaryColors.value.find((c) => c.name === layoutConfig.primary);
        if (primary) applyTheme('primary', primary);
    }
    if (layoutConfig.surface) {
        const surface = surfaces.value.find((s) => s.name === layoutConfig.surface);
        if (surface) applyTheme('surface', surface);
    }
});

// Ripristina le impostazioni del pannello ai valori di default e riapplica tema/menu.
/**
 * Chi amministra l'azienda può fissare l'aspetto che vedranno i colleghi: è una scelta che
 * riguarda tutti, non una preferenza personale, quindi il pulsante compare solo a lui.
 */
const puoImpostarePerAzienda = computed(
    () => !!auth.me?.amministratoreGenerale || !!auth.me?.isSuperAdmin
);
const salvandoPerAzienda = ref(false);
const esisteDefaultAzienda = ref(false);

onMounted(async () => {
    try {
        const { data } = await apiClient.get('/api/v2/aziende/layout-default');
        esisteDefaultAzienda.value = !!data;
    } catch { /* nessun default, o non raggiungibile */ }
});

async function salvaPerAzienda() {
    salvandoPerAzienda.value = true;
    try {
        const { primary, surface, darkTheme: dark, menuMode: mode, menuTheme,
                topbarTheme, menuProfilePosition: profilo } = layoutConfig;
        await apiClient.put('/api/v2/aziende/layout-default', {
            primary, surface, darkTheme: dark, menuMode: mode, menuTheme, topbarTheme,
            menuProfilePosition: profilo
        });
        esisteDefaultAzienda.value = true;
        toast.add({
            severity: 'success',
            summary: t('layout.config.predefinitoSalvato', 'Aspetto predefinito salvato'),
            detail: t('layout.config.loVedranno', 'Lo vedranno i colleghi che non hanno personalizzato il proprio.'),
            life: 4000
        });
    } catch {
        toast.add({ severity: 'error', summary: t('layout.config.salvataggioNonRiuscito', 'Salvataggio non riuscito'), life: 4000 });
    } finally {
        salvandoPerAzienda.value = false;
    }
}

/** Per l'utente: allinearsi ai colori dell'azienda quando vuole, senza che nessuno glieli imponga. */
async function usaAspettoAzienda() {
    try {
        const { data } = await apiClient.get('/api/v2/aziende/layout-default');
        if (!data) return;
        applicaLayout(data, true);   // scelta esplicita dell'utente: si salva
        sincronizzaPannello();
    } catch {
        toast.add({ severity: 'error', summary: t('layout.config.impostazioniAziendaNd', 'Impostazioni azienda non disponibili'), life: 3000 });
    }
}

/** Riallinea i controlli del pannello e il motore dei temi dopo un cambio non manuale. */
function sincronizzaPannello() {
    darkTheme.value = layoutConfig.darkTheme;
    menuMode.value = layoutConfig.menuMode;
    menuProfilePosition.value = layoutConfig.menuProfilePosition;
    const primary = primaryColors.value.find((c) => c.name === layoutConfig.primary);
    if (primary) applyTheme('primary', primary);
    const surface = surfaces.value.find((sx) => sx.name === layoutConfig.surface);
    if (surface) applyTheme('surface', surface);
    document.documentElement.classList.toggle('app-dark', layoutConfig.darkTheme);
    changeMenuMode(layoutConfig.menuMode);
}

function resetSettings() {
    // Dimentica anche la personalizzazione: da qui in poi valgono di nuovo i colori dell'azienda.
    resetLayout();
    sincronizzaPannello();
}
</script>

<template>
  <button
    v-if="simple"
    class="layout-config-button config-link"
    type="button"
    @click="toggleConfigSidebar"
  >
    <i class="pi pi-cog" />
  </button>
  <Drawer
    v-model:visible="layoutState.configSidebarVisible"
    position="right"
    class="layout-config-sidebar w-80"
    :header="t('layout.config.impostazioni', 'Impostazioni')"
    :pt="{
      pcCloseButton: { root: 'ml-auto' }
    }"
  >
    <div class="flex flex-col gap-4">
      <div>
        <span class="text-lg font-semibold">{{ t('layout.config.colorePrimario', 'Colore primario') }}</span>
        <div class="pt-2 flex gap-2 flex-wrap">
          <button
            v-for="primaryColor of primaryColors"
            :key="primaryColor.name"
            :title="primaryColor.name"
            type="button"
            :class="['cursor-pointer w-6 h-6 rounded-full flex flex-shrink-0 items-center justify-center p-0 outline-none outline-offset-1', { 'outline-primary': layoutConfig.primary === primaryColor.name }]"
            :style="{ backgroundColor: `${primaryColor.name === 'noir' ? 'var(--text-color)' : primaryColor.palette['500']}` }"
            @click="updateColors('primary', primaryColor)"
          />
        </div>
      </div>

      <div>
        <span class="text-lg font-semibold">{{ t('layout.config.superficie', 'Superficie') }}</span>
        <div class="pt-2 flex gap-2 flex-wrap">
          <button
            v-for="surface of surfaces"
            :key="surface.name"
            :title="surface.name"
            type="button"
            :class="[
              'cursor-pointer w-6 h-6 rounded-full flex flex-shrink-0 items-center justify-center p-0 outline-none outline-offset-1',
              { 'outline-primary': layoutConfig.surface ? layoutConfig.surface === surface.name : isDarkTheme ? surface.name === 'zinc' : surface.name === 'slate' }
            ]"
            :style="{ backgroundColor: `${surface.palette['500']}` }"
            @click="updateColors('surface', surface)"
          />
        </div>
      </div>

      <div>
        <div class="flex flex-col gap-2">
          <span class="text-lg font-semibold">{{ t('layout.config.temaColore', 'Tema colore') }}</span>
          <SelectButton
            v-model="darkTheme"
            :options="themeOptions"
            option-label="name"
            option-value="value"
            :allow-empty="false"
            size="small"
            @change="toggleDarkMode"
          />
        </div>
      </div>

      <template v-if="!simple">
        <div>
          <div class="flex flex-col gap-2">
            <span class="text-lg font-semibold">{{ t('layout.config.tipoMenu', 'Tipo di menu') }}</span>
            <div class="flex flex-wrap flex-col gap-3">
              <div class="flex">
                <div class="flex items-center gap-2 w-1/2">
                  <RadioButton
                    v-model="menuMode"
                    name="menuMode"
                    value="static"
                    input-id="static"
                    @update:model-value="changeMenuMode"
                  />
                  <label for="static">{{ t('layout.config.statico', 'Statico') }}</label>
                </div>

                <div class="flex items-center gap-2 w-1/2">
                  <RadioButton
                    v-model="menuMode"
                    name="menuMode"
                    value="horizontal"
                    input-id="horizontal"
                    @update:model-value="changeMenuMode"
                  />
                  <label for="horizontal">{{ t('layout.config.orizzontale', 'Orizzontale') }}</label>
                </div>
              </div>
              <div class="flex">
                <div class="flex items-center gap-2 w-1/2">
                  <RadioButton
                    v-model="menuMode"
                    name="menuMode"
                    value="drawer"
                    input-id="drawer"
                    @update:model-value="changeMenuMode"
                  />
                  <label for="drawer">{{ t('layout.config.drawer', 'Drawer') }}</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div class="flex flex-col gap-2">
            <span class="text-lg font-semibold">{{ t('layout.config.profiloMenu', 'Profilo menu') }}</span>
            <div class="flex flex-wrap flex-col gap-4">
              <div class="flex">
                <div class="flex items-center gap-2 w-1/2">
                  <RadioButton
                    v-model="menuProfilePosition"
                    name="menuProfilePosition"
                    value="start"
                    input-id="start"
                    @update:model-value="setProfilePosition"
                  />
                  <label for="start">{{ t('layout.config.inizio', 'Inizio') }}</label>
                </div>

                <div class="flex items-center gap-2 w-1/2">
                  <RadioButton
                    v-model="menuProfilePosition"
                    name="menuProfilePosition"
                    value="end"
                    input-id="end"
                    @update:model-value="setProfilePosition"
                  />
                  <label for="end">{{ t('layout.config.fine', 'Fine') }}</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <span class="text-lg font-semibold">{{ t('layout.config.temiMenu', 'Temi menu') }}</span>
          <div
            v-if="!layoutConfig.darkTheme"
            class="pt-2 flex gap-2 flex-wrap"
          >
            <button
              v-for="(theme, i) in menuThemes"
              :key="i"
              class="cursor-pointer w-6 h-6 rounded-full flex flex-shrink-0 items-center justify-center p-0 outline-none outline-offset-1 shadow"
              :style="{ 'background-color': theme.color }"
              @click="() => (layoutConfig.menuTheme = theme.name)"
            >
              <i
                v-if="theme.name === layoutConfig.menuTheme"
                :class="['pi pi-check', theme.name === layoutConfig.menuTheme && layoutConfig.menuTheme !== 'light' ? 'text-white' : 'text-dark']"
              />
            </button>
          </div>
          <template v-else>
            <p>{{ t('layout.config.temiSoloChiaro', 'I temi del menu sono disponibili solo in modalità chiara: in modalità scura superfici ampie risulterebbero troppo luminose.') }}</p>
          </template>
        </div>

        <div>
          <span class="text-lg font-semibold">{{ t('layout.config.temiBarra', 'Temi barra superiore') }}</span>
          <div class="pt-2 flex gap-2 flex-wrap">
            <button
              v-for="(theme, i) in topbarThemes"
              :key="i"
              class="cursor-pointer w-6 h-6 rounded-full flex flex-shrink-0 items-center justify-center p-0 outline-none outline-offset-1 shadow"
              :style="{ 'background-color': theme.color }"
              @click="() => (layoutConfig.topbarTheme = theme.name)"
            >
              <i
                v-if="theme.name === layoutConfig.topbarTheme"
                :class="['pi pi-check', theme.name === layoutConfig.topbarTheme && layoutConfig.topbarTheme !== 'white' ? 'text-white' : 'text-dark']"
              />
            </button>
          </div>
        </div>
      </template>

      <div class="pt-2">
        <Button
          :label="t('layout.config.ripristina', 'Ripristina predefinite')"
          icon="pi pi-refresh"
          severity="secondary"
          outlined
          size="small"
          class="w-full"
          @click="resetSettings"
        />
        <Button
          v-if="esisteDefaultAzienda"
          :label="t('layout.config.usaColoriAzienda', 'Usa i colori dell\'azienda')"
          icon="pi pi-building"
          severity="secondary"
          outlined
          size="small"
          class="w-full mt-2"
          @click="usaAspettoAzienda"
        />
        <!-- Solo per chi amministra l'azienda: fissa l'aspetto che vedranno i colleghi. -->
        <Button
          v-if="puoImpostarePerAzienda"
          :label="t('layout.config.salvaPerAzienda', 'Salva come predefinite per l\'azienda')"
          icon="pi pi-save"
          size="small"
          class="w-full mt-2"
          :loading="salvandoPerAzienda"
          @click="salvaPerAzienda"
        />
        <small
          v-if="puoImpostarePerAzienda"
          class="block mt-2 text-muted-color"
        >
          {{ t('layout.config.leVedranno', "Le vedranno i colleghi che non hanno personalizzato l'aspetto: chi l'ha fatto mantiene il suo, e può allinearsi quando vuole.") }}
        </small>
      </div>
    </div>
  </Drawer>
</template>
